import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  FormControl,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Textarea,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { ArrowBackIcon, SearchIcon } from "@chakra-ui/icons";
import { BiCheckDouble, BiMessageAdd, BiMessageRoundedDetail, BiSend } from "react-icons/bi";
import { FiPaperclip, FiX } from "react-icons/fi";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "react-query";
import { GlobalContext } from "../../context/Provider";
import LayoutView from "../../components/layout";
import Auth from "../../hoc/Auth";
import { useChatSocket } from "../../chat/ChatSocketProvider";
import {
  createConversationApi,
  getChatWorkersApi,
  getConversationsApi,
  getMessagesApi,
  markConversationReadApi,
  sendMessageApi,
} from "../../api/chat";
import { uploadFilesApi } from "../../api/files";
import FileList, { formatFileSize } from "../../components/files/FileList";

const fullName = (worker) =>
  [worker?.firstName, worker?.lastName].filter(Boolean).join(" ") || "Staff member";
const shortTime = (value) =>
  value
    ? new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(new Date(value))
    : "";
const listTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  const today = new Date();
  return date.toDateString() === today.toDateString()
    ? shortTime(value)
    : new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(date);
};

const ChatPage = () => {
  const { authState } = useContext(GlobalContext);
  const { socket, connected } = useChatSocket();
  const queryClient = useQueryClient();
  const toast = useToast();
  const newChat = useDisclosure();
  const [selectedId, setSelectedId] = useState("");
  const [search, setSearch] = useState("");
  const [messageText, setMessageText] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [typingWorker, setTypingWorker] = useState(false);
  const typingTimer = useRef();
  const bottomRef = useRef();
  const fileInputRef = useRef();

  const conversationsQuery = useQuery(
    "chatConversations",
    () => getConversationsApi({ limit: 100 }),
    { staleTime: 10000, refetchInterval: connected ? false : 15000 },
  );
  const conversations = conversationsQuery.data?.data?.conversations || [];
  const selected = conversations.find((item) => String(item._id) === selectedId);
  const otherWorker = selected?.participants?.find(
    (worker) => String(worker._id) !== String(authState.user?._id),
  );

  const messagesQuery = useInfiniteQuery(
    ["chatMessages", selectedId],
    ({ pageParam }) => getMessagesApi({ conversationId: selectedId, before: pageParam }),
    {
      enabled: Boolean(selectedId),
      getNextPageParam: (lastPage) => lastPage.data.nextCursor || undefined,
      refetchOnWindowFocus: true,
    },
  );
  const messages = useMemo(() => {
    const pages = messagesQuery.data?.pages || [];
    return pages
      .slice()
      .reverse()
      .flatMap((page) => page.data.messages || []);
  }, [messagesQuery.data]);

  const workersQuery = useQuery(
    ["chatWorkers", search],
    () => getChatWorkersApi(search),
    { enabled: newChat.isOpen, staleTime: 10000, keepPreviousData: true },
  );
  const createConversation = useMutation(createConversationApi, {
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries("chatConversations");
      setSelectedId(String(data.conversation._id));
      newChat.onClose();
      setSearch("");
    },
    onError: (error) => toast({ status: "error", title: error.response?.data?.msg || "Could not start chat" }),
  });

  useEffect(() => {
    if (!selectedId || messagesQuery.isLoading) return;
    const markRead = () => {
      if (connected && socket) {
        socket.emit("conversation:read", { conversationId: selectedId }, () => {});
      } else {
        markConversationReadApi(selectedId).catch(() => {});
      }
      queryClient.invalidateQueries("chatUnreadCount");
      queryClient.invalidateQueries("chatConversations");
    };
    markRead();
  }, [selectedId, messages.length, messagesQuery.isLoading, connected, socket, queryClient]);

  useEffect(() => {
    if (!socket || !selectedId) return undefined;
    socket.emit("conversation:join", { conversationId: selectedId }, () => {});
    const onTyping = (payload) => {
      if (payload.conversationId === selectedId && payload.workerId !== String(authState.user?._id)) {
        setTypingWorker(payload.typing);
      }
    };
    socket.on("conversation:typing", onTyping);
    return () => socket.off("conversation:typing", onTyping);
  }, [socket, selectedId, authState.user?._id]);

  useEffect(() => {
    if (messages.length) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, selectedId]);

  const send = async () => {
    const text = messageText.trim();
    if ((!text && !pendingFiles.length) || !selectedId || sending) return;
    setSending(true);
    setMessageText("");
    try {
      const upload = pendingFiles.length ? await uploadFilesApi(pendingFiles, "chat") : null;
      const payload = {
        conversationId: selectedId,
        text,
        fileIds: upload?.data?.files?.map((file) => file._id) || [],
        clientId: `${authState.user?._id}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      };
      if (connected && socket) {
        await new Promise((resolve, reject) => {
          socket.emit("message:send", payload, (response) =>
            response?.ok ? resolve(response) : reject(new Error(response?.message || "Message failed")),
          );
        });
      } else {
        await sendMessageApi(selectedId, payload);
      }
      queryClient.invalidateQueries(["chatMessages", selectedId]);
      queryClient.invalidateQueries("chatConversations");
      setPendingFiles([]);
    } catch (error) {
      setMessageText(text);
      toast({ status: "error", title: error.response?.data?.msg || error.message || "Message was not sent" });
    } finally {
      setSending(false);
    }
  };

  const chooseChatFiles = (event) => {
    const files = Array.from(event.target.files || []);
    const accepted = files.filter((file) => ["image/jpeg", "image/png", "image/webp", "application/pdf"].includes(file.type) && file.size <= 10 * 1024 * 1024);
    if (accepted.length !== files.length) toast({ status: "warning", title: "Some files were skipped", description: "Use JPEG, PNG, WebP or PDF files up to 10MB each." });
    setPendingFiles((current) => [...current, ...accepted].slice(0, 5));
    event.target.value = "";
  };

  const handleTyping = (event) => {
    setMessageText(event.target.value);
    if (!socket || !connected || !selectedId) return;
    socket.emit("conversation:typing", { conversationId: selectedId, typing: true }, () => {});
    window.clearTimeout(typingTimer.current);
    typingTimer.current = window.setTimeout(() => {
      socket.emit("conversation:typing", { conversationId: selectedId, typing: false }, () => {});
    }, 1200);
  };

  return (
    <LayoutView activePage="Chat" noPadding>
      <Flex h="calc(100vh - 76px - 76px)" minH="560px" bg="gray.50">
        <Box
          w={{ base: "full", md: "360px", lg: "400px" }}
          bg="white"
          borderRightWidth={{ md: "1px" }}
          display={{ base: selectedId ? "none" : "block", md: "block" }}
          overflowY="auto"
        >
          <Flex px="5" py="4" align="center" justify="space-between" borderBottomWidth="1px">
            <Box>
              <Text fontSize="xl" fontWeight="800">Messages</Text>
              <HStack spacing="1.5">
                <Box w="7px" h="7px" borderRadius="full" bg={connected ? "green.400" : "orange.400"} />
                <Text fontSize="xs" color="gray.500">{connected ? "Live updates connected" : "Reconnecting…"}</Text>
              </HStack>
            </Box>
            <IconButton icon={<BiMessageAdd />} aria-label="Start new chat" colorScheme="brand" onClick={newChat.onOpen} />
          </Flex>
          {conversationsQuery.isLoading ? (
            <Center py="16"><Spinner color="brand.500" /></Center>
          ) : conversations.length === 0 ? (
            <Center px="8" py="20" textAlign="center" flexDirection="column">
              <Icon as={BiMessageRoundedDetail} boxSize="12" color="gray.300" mb="3" />
              <Text fontWeight="700">No conversations yet</Text>
              <Text color="gray.500" fontSize="sm" mt="1">Start a private text conversation with another staff member.</Text>
              <Button mt="5" colorScheme="brand" onClick={newChat.onOpen}>Start a chat</Button>
            </Center>
          ) : (
            <VStack spacing="0" align="stretch">
              {conversations.map((conversation) => {
                const worker = conversation.participants.find((item) => String(item._id) !== String(authState.user?._id));
                const active = String(conversation._id) === selectedId;
                return (
                  <Flex
                    key={conversation._id}
                    px="4" py="3.5" gap="3" cursor="pointer"
                    bg={active ? "brand.50" : "white"}
                    borderLeftWidth="3px" borderLeftColor={active ? "brand.500" : "transparent"}
                    _hover={{ bg: active ? "brand.50" : "gray.50" }}
                    onClick={() => setSelectedId(String(conversation._id))}
                  >
                    <Avatar size="md" name={fullName(worker)} src={worker?.image} />
                    <Box flex="1" minW="0">
                      <Flex justify="space-between" gap="2">
                        <Text fontWeight={conversation.unreadCount ? "800" : "700"} noOfLines={1}>{fullName(worker)}</Text>
                        <Text fontSize="xs" color="gray.500" flexShrink="0">{listTime(conversation.lastMessageAt)}</Text>
                      </Flex>
                      <Flex align="center" justify="space-between" gap="2" mt="1">
                        <Text fontSize="sm" color="gray.500" noOfLines={1}>
                          {conversation.lastMessage?.sender?._id === authState.user?._id ? "You: " : ""}
                          {conversation.lastMessage?.text || "Conversation started"}
                        </Text>
                        {conversation.unreadCount > 0 && <Badge colorScheme="red" borderRadius="full">{conversation.unreadCount > 99 ? "99+" : conversation.unreadCount}</Badge>}
                      </Flex>
                    </Box>
                  </Flex>
                );
              })}
            </VStack>
          )}
        </Box>

        <Flex flex="1" minW="0" direction="column" display={{ base: selectedId ? "flex" : "none", md: "flex" }}>
          {!selected ? (
            <Center h="full" flexDirection="column" px="8" textAlign="center">
              <Icon as={BiMessageRoundedDetail} boxSize="16" color="gray.300" />
              <Text mt="4" fontSize="xl" fontWeight="800">Your staff conversations</Text>
              <Text mt="1" color="gray.500">Choose a conversation or start a new one.</Text>
            </Center>
          ) : (
            <>
              <Flex bg="white" px={{ base: "3", md: "5" }} py="3" align="center" borderBottomWidth="1px" gap="3">
                <IconButton display={{ base: "inline-flex", md: "none" }} icon={<ArrowBackIcon />} variant="ghost" aria-label="Back to conversations" onClick={() => setSelectedId("")} />
                <Avatar size="sm" name={fullName(otherWorker)} src={otherWorker?.image} />
                <Box minW="0">
                  <Text fontWeight="800" noOfLines={1}>{fullName(otherWorker)}</Text>
                  <Text fontSize="xs" color="gray.500" noOfLines={1}>{otherWorker?.unit}{otherWorker?.subUnit ? ` · ${otherWorker.subUnit}` : ""}</Text>
                </Box>
              </Flex>
              <Box flex="1" overflowY="auto" px={{ base: "3", md: "6", lg: "12" }} py="5">
                {messagesQuery.hasNextPage && (
                  <Center mb="5"><Button size="sm" variant="outline" isLoading={messagesQuery.isFetchingNextPage} onClick={() => messagesQuery.fetchNextPage()}>Load older messages</Button></Center>
                )}
                {messagesQuery.isLoading ? <Center h="full"><Spinner color="brand.500" /></Center> : (
                  <VStack spacing="3" align="stretch">
                    {messages.map((message) => {
                      const mine = String(message.sender?._id || message.sender) === String(authState.user?._id);
                      return (
                        <Flex key={message._id} justify={mine ? "flex-end" : "flex-start"}>
                          <Box maxW={{ base: "85%", md: "68%" }} bg={mine ? "brand.600" : "white"} color={mine ? "white" : "gray.800"} px="4" py="2.5" borderRadius={mine ? "18px 18px 4px 18px" : "18px 18px 18px 4px"} boxShadow="sm">
                            <Text whiteSpace="pre-wrap" overflowWrap="anywhere">{message.text}</Text>
                            <FileList files={message.files || []} compact />
                            <HStack mt="1" justify="flex-end" spacing="1" opacity="0.75">
                              <Text fontSize="10px">{shortTime(message.createdAt)}</Text>
                              {mine && <Icon as={BiCheckDouble} boxSize="3.5" color={message.readBy?.length > 1 ? "cyan.200" : "inherit"} />}
                            </HStack>
                          </Box>
                        </Flex>
                      );
                    })}
                    {typingWorker && <Text fontSize="sm" color="gray.500" fontStyle="italic">{fullName(otherWorker)} is typing…</Text>}
                    <div ref={bottomRef} />
                  </VStack>
                )}
              </Box>
              <Box bg="white" borderTopWidth="1px" px={{ base: "3", md: "6" }} py="3">
                {!!pendingFiles.length && <HStack mb="2" overflowX="auto" pb="1">{pendingFiles.map((file, index) => <HStack key={`${file.name}-${index}`} flexShrink="0" bg="gray.100" borderRadius="lg" px="2" py="1"><Text fontSize="xs" maxW="180px" noOfLines={1}>{file.name}</Text><Text fontSize="10px" color="gray.500">{formatFileSize(file.size)}</Text><IconButton size="xs" variant="ghost" icon={<FiX />} aria-label="Remove attachment" onClick={() => setPendingFiles((items) => items.filter((_, itemIndex) => itemIndex !== index))} /></HStack>)}</HStack>}
                <FormControl>
                  <HStack align="flex-end">
                    <input ref={fileInputRef} type="file" hidden multiple accept="image/jpeg,image/png,image/webp,application/pdf" onChange={chooseChatFiles} />
                    <IconButton variant="ghost" icon={<FiPaperclip />} aria-label="Attach files" onClick={() => fileInputRef.current?.click()} isDisabled={sending || pendingFiles.length >= 5} />
                    <Textarea value={messageText} onChange={handleTyping} placeholder={`Message ${fullName(otherWorker)}`} resize="none" minH="44px" maxH="120px" rows={1} maxLength={4000} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); send(); } }} />
                    <IconButton colorScheme="brand" icon={<BiSend />} aria-label="Send message" isLoading={sending} isDisabled={!messageText.trim() && !pendingFiles.length} onClick={send} />
                  </HStack>
                  <Text fontSize="xs" color="gray.400" mt="1">Enter to send · Shift + Enter for a new line</Text>
                </FormControl>
              </Box>
            </>
          )}
        </Flex>
      </Flex>

      <Modal isOpen={newChat.isOpen} onClose={newChat.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Start a conversation</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="6">
            <InputGroup mb="4">
              <InputLeftElement pointerEvents="none"><SearchIcon color="gray.400" /></InputLeftElement>
              <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search staff by name, unit or staff ID" autoFocus />
            </InputGroup>
            <Divider />
            {workersQuery.isLoading ? <Center py="10"><Spinner /></Center> : (
              <VStack align="stretch" spacing="1" mt="2" maxH="420px" overflowY="auto">
                {(workersQuery.data?.data?.workers || []).map((worker) => (
                  <Button key={worker._id} variant="ghost" h="auto" py="3" justifyContent="flex-start" isLoading={createConversation.isLoading && createConversation.variables === worker._id} onClick={() => createConversation.mutate(worker._id)}>
                    <HStack textAlign="left"><Avatar size="sm" name={fullName(worker)} src={worker.image} /><Box><Text fontWeight="700">{fullName(worker)}</Text><Text fontSize="xs" color="gray.500">{worker.unit}{worker.subUnit ? ` · ${worker.subUnit}` : ""}</Text></Box></HStack>
                  </Button>
                ))}
                {!workersQuery.isLoading && !(workersQuery.data?.data?.workers || []).length && <Center py="10"><Text color="gray.500">No active staff found.</Text></Center>}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </LayoutView>
  );
};

export default Auth(ChatPage);
